import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Layers, ArrowRight } from "lucide-react";

/**
 * ITER170 (Feb 2026) — Diálogo que avisa al usuario que el lote elegido no
 * alcanza para la cantidad ingresada y propone dividir automáticamente en
 * varios lotes siguiendo FEFO.
 *
 * Props:
 *  - open: boolean
 *  - split: resultado de computeFEFOSplit(linea, cant)
 *  - productoNombre
 *  - onConfirm: () => void  (aplica el split)
 *  - onCancel:  () => void  (cierra sin aplicar; deja al usuario ajustar manual)
 */
export function AutoSplitFEFODialog({ open, split, productoNombre, onConfirm, onCancel }) {
  if (!split) return null;
  const plan = split.plan || [];
  const faltante = split.faltante || 0;
  const cantidadDeseada = split.cantidad_deseada || 0;
  const cantidadAsignada = split.cantidad_asignada || 0;
  const unidad = split.unidad || "unidad";
  const unidadLabel = unidad === "caja" ? "cajas" : "unidades";
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel && onCancel()}>
      <DialogContent className="max-w-md" data-testid="auto-split-fefo-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers size={18} className="text-[#8a5500]" />
            Dividir en múltiples lotes
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="rounded-md bg-[#fef7ec] border border-[#f5c477] p-3">
            <p className="text-[#8a5500]">
              El lote seleccionado no tiene stock suficiente para despachar{" "}
              <strong>{cantidadDeseada} {unidadLabel}</strong> de{" "}
              <strong>{productoNombre || "este producto"}</strong>.
            </p>
            <p className="text-xs text-[#5c7368] mt-1">
              Se propone completar automáticamente con los siguientes lotes disponibles
              (orden FEFO — el que vence antes primero).
            </p>
          </div>
          <div className="border border-[#d1dbd6] rounded-md overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-[#f7f9f8] text-[#5c7368] uppercase tracking-wider">
                <tr>
                  <th className="text-left p-2">Lote</th>
                  <th className="text-left p-2">Vence</th>
                  <th className="text-right p-2">Disp. ({unidadLabel})</th>
                  <th className="text-right p-2 text-[#205c45]">Tomar ({unidadLabel})</th>
                </tr>
              </thead>
              <tbody>
                {plan.map((p, i) => (
                  <tr key={p.lote_id} className={i === 0 ? "" : "bg-[#f7fbf9]"} data-testid={`split-row-${i}`}>
                    <td className="p-2 font-mono">{p.numero_lote || "—"}</td>
                    <td className="p-2 text-[#5c7368]">{p.fecha_vencimiento || "—"}</td>
                    <td className="p-2 text-right tabular-nums text-[#5c7368]">{p.cantidad_disponible}</td>
                    <td className="p-2 text-right tabular-nums font-semibold text-[#205c45]">{p.cantidad_a_tomar}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-[#e9f2ee] font-semibold">
                  <td colSpan={3} className="p-2 text-right">Total a despachar</td>
                  <td className="p-2 text-right tabular-nums text-[#205c45]">{cantidadAsignada} {unidadLabel}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          {faltante > 0 && (
            <div className="rounded-md bg-[#fdecea] border border-[#d99584] p-2 text-xs text-[#7a2514]">
              ⚠ Aún faltan <strong>{faltante} {unidadLabel}</strong> y no hay más stock disponible.
              Se dividirá lo posible; deberá gestionar el resto por separado.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="split-cancel-btn">Cancelar</Button>
          <Button onClick={onConfirm} className="bg-[#205c45] hover:bg-[#1a4a37]" data-testid="split-confirm-btn">
            <ArrowRight size={14} className="mr-2" />
            Aplicar división
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
